import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * BlurFade — Emil Kowalski-style blur+fade reveal on scroll.
 * @param {{ children: React.ReactNode, delay?: number, direction?: 'up'|'down'|'left'|'right', className?: string, amount?: number }} props
 */
export function BlurFade({
  children,
  delay = 0,
  direction = 'up',
  className = '',
  amount = 24,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const directionMap = {
    up: { y: amount },
    down: { y: -amount },
    left: { x: amount },
    right: { x: -amount },
  };

  return (
    <motion.div
      ref={ref}
      initial={{
        opacity: 0,
        filter: 'blur(6px)',
        ...directionMap[direction],
      }}
      animate={
        isInView
          ? { opacity: 1, filter: 'blur(0px)', x: 0, y: 0 }
          : undefined
      }
      transition={{
        duration: 0.7,
        delay,
        ease: [0.21, 0.47, 0.32, 0.98],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Stagger container — wraps BlurFade children with incremental delays.
 */
export function BlurFadeStagger({
  children,
  baseDelay = 0,
  stagger = 0.1,
  direction = 'up',
  className = '',
}) {
  const items = Array.isArray(children) ? children : [children];

  return (
    <div className={className}>
      {items.map((child, i) => (
        <BlurFade key={i} delay={baseDelay + i * stagger} direction={direction}>
          {child}
        </BlurFade>
      ))}
    </div>
  );
}
